import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Banner, Button, Dialog, EmptyState, Field, Input, PageHeader, Skeleton, SourceChip, StatusBadge, Tabs, Textarea, apiFetch, useApi, useApiMutation, useCurrentUser, useT, useToast } from "@iga/ui";
import type { AccessView, RoleAssignment, EntitlementAssignment, Account, RequestDetail, ItemKind } from "../lib/types";
import { REQUEST_KEYS, daysUntil, errMsg, useFmt } from "../lib/hooks";
import { LoadError, NoIdentity, PortalRisk, ValidTo } from "../components/common";

type Tab = "roles" | "entitlements" | "accounts";
interface ExtendTarget { kind: ItemKind; targetId: string; displayName: string; validTo: string | null; assignmentId: string }

/** Assignment status for the badge: "expiring" inside 30 days (with the day count), otherwise the stored status. */
function AssignmentStatus({ status, validTo }: { status: string; validTo: string | null }) {
  const days = daysUntil(validTo);
  if (status === "active" && days !== null && days >= 0 && days <= 30) return <StatusBadge domain="assignment" status="expiring" audience="portal" params={{ days }} compact />;
  return <StatusBadge domain="assignment" status={status} audience="portal" compact />;
}

/** Extension dialog: creates an access request with a single `extend` item. */
export function ExtendDialog({ target, beneficiaryId, onClose }: { target: ExtendTarget | null; beneficiaryId?: string; onClose: () => void }) {
  const t = useT();
  const fmt = useFmt();
  const toast = useToast();
  const nav = useNavigate();
  const [validTo, setValidTo] = useState("");
  const [justification, setJustification] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    const base = target.validTo ? new Date(target.validTo) : new Date();
    base.setDate(base.getDate() + 90);
    setValidTo(base.toISOString().slice(0, 10)); setJustification(""); setError(null);
  }, [target]);
  const m = useApiMutation<void, RequestDetail>(async () => apiFetch<RequestDetail>("/api/portal/requests", { method: "POST", json: { beneficiaryId, items: [{ kind: target!.kind, targetId: target!.targetId, action: "extend", validTo: new Date(`${validTo}T23:59:59`).toISOString() }], justification } }), REQUEST_KEYS);
  function submit() {
    if (!justification.trim()) { setError(t("p.request.justificationRequired")); return; }
    m.mutate(undefined, { onSuccess: (r) => { toast.push(t("p.access.extendSent"), { tone: "ok" }); onClose(); nav(`/requests/${r.id}`); }, onError: (e) => toast.push(errMsg(e), { tone: "bad" }) });
  }
  return (
    <Dialog open={!!target} onClose={onClose} title={t("p.access.extendTitle", { item: target?.displayName ?? "" })} size="sm" footer={<>
      <Button variant="secondary" onClick={onClose}>{t("ui.cancel")}</Button>
      <Button loading={m.isPending} disabled={!validTo} onClick={submit}>{t("p.request.send")}</Button>
    </>}>
      <div className="flex flex-col gap-3">
        <p className="text-fg-2">{t("p.access.currentEnd", { date: target?.validTo ? fmt.day(target.validTo) : t("p.access.noEndDate") })}</p>
        <Field label={t("p.access.newEnd")} htmlFor="ext-date"><Input id="ext-date" type="date" value={validTo} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setValidTo(e.target.value)} /></Field>
        <Field label={t("p.access.whyStill")} hint={t("p.request.whyHint")} error={error} htmlFor="ext-why"><Textarea id="ext-why" value={justification} onChange={(e) => { setJustification(e.target.value); setError(null); }} invalid={!!error} rows={3} /></Field>
        <LoadError error={m.error} title={t("p.request.sendFailed")} />
      </div>
    </Dialog>
  );
}

function groupBy<T>(xs: T[], key: (x: T) => string): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const x of xs) { const k = key(x); m.set(k, [...(m.get(k) ?? []), x]); }
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function Row({ name, description, source, risk, validTo, status, actions }: { name: string; description?: string | null; source: string; risk: RoleAssignment["role"]["risk"]; validTo: string | null; status: string; actions?: React.ReactNode }) {
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1"><span className="font-semibold">{name}</span><PortalRisk risk={risk} /></div>
        {description && <div className="text-small text-fg-3">{description}</div>}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-small text-fg-2"><SourceChip source={source === "certification" ? "manual" : source} /><ValidTo value={validTo} /><AssignmentStatus status={status} validTo={validTo} /></div>
      </div>
      {actions && <div className="flex flex-wrap gap-2 sm:flex-none">{actions}</div>}
    </li>
  );
}

/** Roles / entitlements / accounts of an identity. Reused by the team and service-desk detail pages. */
export function AccessTables({ data, onExtend, onRevoke, revokeLabel }: { data: AccessView; onExtend?: (t: ExtendTarget) => void; onRevoke?: (t: { assignmentId?: string; entitlementAssignmentId?: string; displayName: string }) => void; revokeLabel?: string }) {
  const t = useT();
  const fmt = useFmt();
  const [tab, setTab] = useState<Tab>("roles");
  const roleGroups = useMemo(() => groupBy(data.roles, (r) => r.role.application ?? t("p.access.otherRoles")), [data.roles, t]);
  const entGroups = useMemo(() => groupBy(data.entitlements, (e) => e.entitlement.system), [data.entitlements]);
  const canExtend = (a: { source: string; status: string }) => a.status === "active" && ["request", "manual", "certification", "import"].includes(a.source);
  const Group = ({ title, children }: { title: string; children: React.ReactNode }) => <section className="rounded-card border border-line bg-surface overflow-hidden"><h3 className="border-b border-line bg-surface-2 px-4 py-2 text-label text-fg-3">{title}</h3><ul className="divide-y divide-line">{children}</ul></section>;
  return (
    <div className="flex flex-col gap-4">
      <Tabs<Tab> ariaLabel={t("p.nav.myAccess")} value={tab} onChange={setTab} tabs={[{ key: "roles", label: t("p.access.roles"), count: data.roles.length }, { key: "entitlements", label: t("p.access.entitlements"), count: data.entitlements.length }, { key: "accounts", label: t("p.access.accounts"), count: data.accounts.length }]} />
      {tab === "roles" && (roleGroups.length === 0 ? <EmptyState kind="first-use" title={t("p.access.noRoles")} body={t("p.access.noRolesBody")} /> : roleGroups.map(([app, rows]) => (
        <Group key={app} title={app}>
          {rows.map((r: RoleAssignment) => (
            <Row key={r.assignmentId} name={r.role.displayName} description={r.role.description} source={r.source} risk={r.role.risk} validTo={r.validTo} status={r.status}
              actions={<>
                {onExtend && canExtend(r) && r.validTo && <Button size="touch" variant="secondary" onClick={() => onExtend({ kind: "role", targetId: r.role.id, displayName: r.role.displayName, validTo: r.validTo, assignmentId: r.assignmentId })}>{t("p.access.extend")}</Button>}
                {onRevoke && r.revocable && r.status === "active" && <Button size="touch" variant="secondary" onClick={() => onRevoke({ assignmentId: r.assignmentId, displayName: r.role.displayName })}>{revokeLabel ?? t("p.access.remove")}</Button>}
              </>} />
          ))}
        </Group>
      )))}
      {tab === "entitlements" && (entGroups.length === 0 ? <EmptyState kind="all-done" title={t("p.access.noEntitlements")} body={t("p.access.noEntitlementsBody")} /> : entGroups.map(([sys, rows]) => (
        <Group key={sys} title={sys}>
          {rows.map((e: EntitlementAssignment) => (
            <Row key={e.assignmentId} name={e.entitlement.displayName} description={e.entitlement.description} source={e.source} risk={e.entitlement.risk} validTo={e.validTo} status={e.status}
              actions={<>
                {onExtend && canExtend(e) && e.validTo && <Button size="touch" variant="secondary" onClick={() => onExtend({ kind: "entitlement", targetId: e.entitlement.id, displayName: e.entitlement.displayName, validTo: e.validTo, assignmentId: e.assignmentId })}>{t("p.access.extend")}</Button>}
                {onRevoke && e.revocable && e.status === "active" && <Button size="touch" variant="secondary" onClick={() => onRevoke({ entitlementAssignmentId: e.assignmentId, displayName: e.entitlement.displayName })}>{revokeLabel ?? t("p.access.remove")}</Button>}
              </>} />
          ))}
        </Group>
      )))}
      {tab === "accounts" && (data.accounts.length === 0 ? <EmptyState kind="all-done" title={t("p.access.noAccounts")} body={t("p.access.noAccountsBody")} /> : data.accounts.map((a: Account) => (
        <section key={a.id} className="rounded-card border border-line bg-surface overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
            <h3 className="text-h3">{a.system.displayName}</h3>
            <span className="text-mono text-fg-2">{a.userName ?? a.nativeId}</span>
            <StatusBadge domain="account" status={a.locked ? "locked" : a.status === "enabled" ? "present" : a.status} audience="portal" compact />
            {a.lastSeenAt && <span className="ml-auto text-small text-fg-3">{t("p.access.lastRead", { when: fmt.day(a.lastSeenAt) })}</span>}
          </div>
          {a.entitlements.length === 0 ? <p className="p-4 text-fg-3">{t("p.access.noAccountEntitlements")}</p> : (
            <ul className="divide-y divide-line">
              {a.entitlements.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                  <span className="font-semibold">{e.displayName}</span><span className="text-small text-fg-3">{e.type}</span><PortalRisk risk={e.risk} />
                  <span className="ml-auto flex items-center gap-2"><SourceChip source={e.source === "external" ? "external" : "request"}>{e.source === "external" ? t("source.external") : t("p.access.viaPlatform")}</SourceChip>{e.state !== "present" && <StatusBadge domain="account" status={e.state} audience="portal" compact />}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )))}
    </div>
  );
}

export function MyAccessPage() {
  const t = useT();
  const me = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const access = useApi<AccessView>(["me-access"], "/api/portal/me/access", undefined, { enabled: !!me?.identity });
  const [extend, setExtend] = useState<ExtendTarget | null>(null);

  // Deep link from expiry notifications: /my-access?extend=<kind>:<assignmentId>
  useEffect(() => {
    const p = params.get("extend");
    if (!p || !access.data) return;
    const [kind, assignmentId = ""] = p.split(":");
    const r = kind === "role" ? access.data.roles.find((x) => x.assignmentId === assignmentId) : undefined;
    const e = kind === "entitlement" ? access.data.entitlements.find((x) => x.assignmentId === assignmentId) : undefined;
    if (r) setExtend({ kind: "role", targetId: r.role.id, displayName: r.role.displayName, validTo: r.validTo, assignmentId });
    else if (e) setExtend({ kind: "entitlement", targetId: e.entitlement.id, displayName: e.entitlement.displayName, validTo: e.validTo, assignmentId });
    setParams({}, { replace: true });
  }, [params, access.data, setParams]);

  if (!me?.identity) return <><PageHeader portal title={t("p.nav.myAccess")} /><NoIdentity /></>;
  if (access.isLoading) return <><PageHeader portal title={t("p.nav.myAccess")} /><div className="rounded-card border border-line bg-surface p-4"><Skeleton rows={4} /></div></>;
  if (access.error) return <><PageHeader portal title={t("p.nav.myAccess")} /><LoadError error={access.error} onRetry={() => void access.refetch()} /></>;
  const d = access.data!;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader portal title={t("p.nav.myAccess")} subtitle={t("p.access.subtitle")} />
      {d.expiringSoon.length > 0 && <Banner tone="warn" title={t("p.access.expiringTitle", { count: d.expiringSoon.length })} body={t("p.access.expiringBody")} />}
      {d.openViolations > 0 && <Banner tone="warn" title={t("p.access.conflictsTitle", { count: d.openViolations })} body={t("p.access.conflictsBody")} />}
      <AccessTables data={d} onExtend={setExtend} />
      <ExtendDialog target={extend} onClose={() => setExtend(null)} />
    </div>
  );
}
